'use client'

import React, { useState, useRef, useEffect } from 'react'
import { useRegion } from './RegionContext'
import { Copy, CheckCircle } from 'lucide-react'
import { AppTooltip } from '@/components/ui/AppTooltip'

const CopyCell = ({ text }: { text: string }) => {
  const { notifyRegionCopy } = useRegion()
  const [copied, setCopied] = useState(false)
  const isCopying = useRef(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const handleCopy = async () => {
    if (isCopying.current) return
    isCopying.current = true

    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      notifyRegionCopy(text)

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        setCopied(false)
        isCopying.current = false
      }, 2000)
    } catch (err) {
      console.error('Failed to copy text: ', err)
      isCopying.current = false
    }
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <div className="group flex items-center justify-between gap-2">
      <span className="font-mono text-sm">{text}</span>
      <AppTooltip content="Double-check this is your workspace region" side="top">
        <span className="inline-flex">
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex h-6 w-6 items-center justify-center p-0 text-[var(--l3-foreground)] opacity-0 transition-opacity hover:bg-transparent hover:text-[var(--l1-foreground)] group-hover:opacity-100"
            title="Copy to clipboard"
            aria-label="Copy to clipboard"
          >
            {copied ? (
              <CheckCircle className="h-4 w-4 text-green-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </button>
        </span>
      </AppTooltip>
    </div>
  )
}

const RegionTable = () => {
  const { regions, isLoading } = useRegion()

  if (isLoading) {
    return (
      <div className="w-full overflow-x-auto">
        <div className="h-32 w-full animate-pulse rounded bg-[var(--l3-background)]" />
      </div>
    )
  }

  // Flatten the data for the table
  const tableData = regions.flatMap((region) =>
    region.clusters.map((cluster) => ({
      name: region.name,
      cloudRegion: cluster.cloud_region,
      provider: cluster.cloud_provider,
      dns: `https://ingest.${region.dns}`,
    }))
  )

  return (
    <div className="my-8 w-full overflow-x-auto">
      <table className="w-full table-auto border-collapse text-left">
        <thead>
          <tr>
            <th className="border-b border-[var(--l2-border)] px-6 py-4 font-semibold text-[var(--l1-foreground)]">
              Name
            </th>
            <th className="border-b border-[var(--l2-border)] px-6 py-4 font-semibold text-[var(--l1-foreground)]">
              Cloud Provider
            </th>
            <th className="border-b border-[var(--l2-border)] px-6 py-4 font-semibold text-[var(--l1-foreground)]">
              Cloud Region
            </th>
            <th className="border-b border-[var(--l2-border)] px-6 py-4 font-semibold text-[var(--l1-foreground)]">
              Ingestion Endpoint
            </th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr
              key={`${row.cloudRegion}-${index}`}
              className="border-b border-[var(--l2-border)] last:border-0"
            >
              <td className="px-6 py-4 text-[var(--l1-foreground)]">{row.name}</td>
              <td className="px-6 py-4 uppercase text-[var(--l1-foreground)]">{row.provider}</td>
              <td className="px-6 py-4 text-[var(--l1-foreground)]">{row.cloudRegion}</td>
              <td className="px-6 py-4 text-[var(--l1-foreground)]">
                <CopyCell text={row.dns} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default RegionTable
